import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, History, FileText } from "lucide-react";
import api from "@/lib/api";
import { Panel, StatusPill, EmptyState, Loading, SectionLabel } from "@/components/app/primitives";
import { shortDate, relativeTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ARMS = ["Technology and Data", "Investments and Capital", "Venture Building", "Media and Community"];
const ACCESS_LABELS = { studio: "Studio", confidential: "Confidential", advisor: "Advisor" };

function accessTone(level) {
  if (level === "confidential") return "blocked";
  if (level === "advisor") return "accent";
  return "neutral";
}

function AddDocumentDialog({ ventures, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ title: "", doc_type: "Document", venture_id: "none", arm: ARMS[0], access_level: "studio", file_reference: "", summary: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/documents", { ...f, venture_id: f.venture_id === "none" ? null : f.venture_id });
      toast.success("Document added");
      setOpen(false);
      setF({ title: "", doc_type: "Document", venture_id: "none", arm: ARMS[0], access_level: "studio", file_reference: "", summary: "" });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add document"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-document-button" className="h-9 bg-primary hover:bg-[#2746c0] gap-1.5"><Plus className="h-4 w-4" strokeWidth={2} /> Add document</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">Add document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Title</Label>
            <Input data-testid="document-title-input" value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Type</Label>
              <Input value={f.doc_type} onChange={(e) => set("doc_type", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">File reference</Label>
              <Input value={f.file_reference} onChange={(e) => set("file_reference", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Venture</Label>
              <Select value={f.venture_id} onValueChange={(v) => set("venture_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Studio wide</SelectItem>
                  {ventures.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Arm</Label>
              <Select value={f.arm} onValueChange={(v) => set("arm", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ARMS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Access</Label>
              <Select value={f.access_level} onValueChange={(v) => set("access_level", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(ACCESS_LABELS).map((a) => <SelectItem key={a} value={a}>{ACCESS_LABELS[a]}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Summary</Label>
            <Textarea value={f.summary} onChange={(e) => set("summary", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter><Button data-testid="document-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentDetail({ docId, onChanged, trigger }) {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDoc((await api.get(`/documents/${docId}`)).data); }
    catch (e) { toast.error("Could not open document"); }
    finally { setLoading(false); }
  }, [docId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addVersion = async () => {
    setSaving(true);
    try {
      await api.post(`/documents/${docId}/versions`, { file_reference: ref, note });
      setRef(""); setNote("");
      await load(); onChanged();
      toast.success("New version recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add version"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        {loading || !doc ? <Loading /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{doc.title} <span className="font-mono text-sm text-muted-foreground">v{doc.version}</span></DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill dot={false} tone="neutral" label={doc.doc_type} />
                {doc.venture_name && <StatusPill dot={false} tone="accent" label={doc.venture_name} />}
                {doc.arm && <StatusPill dot={false} tone="neutral" label={doc.arm} />}
                <StatusPill dot={false} tone={accessTone(doc.access_level)} label={ACCESS_LABELS[doc.access_level] || doc.access_level} />
              </div>
              {doc.summary && <p className="text-sm text-muted-foreground">{doc.summary}</p>}
              <div>
                <SectionLabel>Versions</SectionLabel>
                <ul className="mt-2 divide-y divide-border rounded-md border border-border">
                  {(doc.versions || []).map((v) => (
                    <li key={v.version} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-foreground font-mono">v{v.version} <span className="text-muted-foreground">{v.file_reference}</span></span>
                      <span className="text-xs text-muted-foreground">{v.note} <span className="text-muted-foreground/50">/</span> {shortDate(v.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {doc.can_edit && (
                <div className="rounded-md border border-border bg-background/40 p-3 space-y-2">
                  <SectionLabel>Record a new version</SectionLabel>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={`v${(doc.version || 0) + 1}`} className="font-mono" />
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" />
                  </div>
                  <Button data-testid="detail-add-version" onClick={addVersion} disabled={saving} className="h-8 bg-primary hover:bg-[#2746c0] gap-1.5"><History className="h-3.5 w-3.5" strokeWidth={1.75} /> {saving ? "Saving" : "Add version"}</Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function th(children, extra = "") { return <th className={`px-4 py-2.5 font-medium ${extra}`}>{children}</th>; }

export default function Documents() {
  const [data, setData] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [arm, setArm] = useState("all");
  const [venture, setVenture] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (arm !== "all") params.arm = arm;
      if (venture !== "all") params.venture = venture;
      const res = await api.get("/documents", { params });
      setData(res.data);
    } catch (e) { toast.error("Could not load documents."); } finally { setLoading(false); }
  }, [search, arm, venture]);

  useEffect(() => {
    api.get("/ventures").then((r) => setVentures(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const docs = data?.documents || [];
  const venturesMap = useMemo(() => Object.fromEntries(ventures.map((v) => [v.id, v.name])), [ventures]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          <Input data-testid="documents-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents" className="pl-9" />
        </div>
        <Select value={arm} onValueChange={setArm}>
          <SelectTrigger data-testid="documents-arm-filter" className="w-56"><SelectValue placeholder="All arms" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All arms</SelectItem>{ARMS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={venture} onValueChange={setVenture}>
          <SelectTrigger data-testid="documents-venture-filter" className="w-48"><SelectValue placeholder="All ventures" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All ventures</SelectItem>{ventures.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
        </Select>
        {data?.can_create && <AddDocumentDialog ventures={ventures} onSaved={load} />}
      </div>

      <Panel title="Document library" testId="document-library">
        {loading ? <Loading /> : docs.length === 0 ? (
          <EmptyState message="No documents match your filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
                {th("Title")}{th("Type")}{th("Venture")}{th("Arm")}{th("Access")}{th("Updated")}{th("Version", "text-right")}
              </tr></thead>
              <tbody className="divide-y divide-border">
                {docs.map((d) => (
                  <DocumentDetail key={d.id} docId={d.id} onChanged={load}
                    trigger={(
                      <tr data-testid="document-row" className="hover:bg-white/[0.03] cursor-pointer">
                        <td className="px-4 py-3 text-foreground"><span className="inline-flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />{d.title}</span></td>
                        <td className="px-4 py-3 text-muted-foreground">{d.doc_type}</td>
                        <td className="px-4 py-3 text-muted-foreground">{d.venture_name || venturesMap[d.venture_id] || "Studio wide"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{d.arm || "\u00B7"}</td>
                        <td className="px-4 py-3"><StatusPill dot={false} tone={accessTone(d.access_level)} label={ACCESS_LABELS[d.access_level] || d.access_level} /></td>
                        <td className="px-4 py-3 text-muted-foreground">{relativeTime(d.updated_at)}</td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">v{d.version}</td>
                      </tr>
                    )} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
